import React, { createContext, useContext, ReactNode } from 'react';

// Simplified theme classes based on demo UI styling
interface ThemeClasses {
  mainBg: string;
  cardBg: string;
  secondaryCardBg: string;
  border: string;
  focusBorder: string;
  primaryText: string;
  secondaryText: string;
  mutedText: string;
  input: string;
  inputFocus: string;
  hover: string;
  cardHover: string;
  buttonPrimary: string;
  buttonSecondary: string;
  errorText: string;
  successText: string;
}

interface ThemeContextType {
  themeClasses: ThemeClasses;
}

// Demo UI inspired theme classes - clean, modern, light theme only
const themeClasses: ThemeClasses = {
  mainBg: 'bg-gray-50',
  cardBg: 'bg-white',
  secondaryCardBg: 'bg-gray-100',
  border: 'border border-gray-200',
  focusBorder: 'border-blue-500',
  primaryText: 'text-gray-900',
  secondaryText: 'text-gray-600',
  mutedText: 'text-gray-500',
  input: 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500 rounded-md',
  inputFocus: 'border-blue-500 ring-blue-500',
  hover: 'hover:bg-gray-50',
  cardHover: 'hover:bg-gray-50',
  buttonPrimary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 rounded-md px-4 py-2',
  buttonSecondary: 'bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-500 rounded-md px-4 py-2',
  errorText: 'text-red-500',
  successText: 'text-green-500'
};

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const value: ThemeContextType = {
    themeClasses
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Export the hook as a named export for HMR compatibility
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}; 